import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import Loader from 'components/Loader';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyGetCompaniesWithFiltersQuery } from 'store/api/companies';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import { useGetPricelistByIdQuery } from 'store/api/pricelists';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { SelectType } from 'types/forms';
import { JobRolesListElement } from 'types/jobRoles';
import { HourlyRate, PricelistDto, PricingKind, ScopeType } from 'types/pricelists';
import { errorAlert } from 'utils/alert';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { EmployeeRatesEditForm } from './EmployeeRatesEditForm';
import {
    editorPricingKind,
    inForcePeriodOf,
    PeriodDraft,
    periodsFromList,
    toPeriodWrites,
    validatePeriodDates,
} from './employeePricing';
import { HourlyRatesEditForm } from './HourlyRatesEditForm';
import { useConfirmPastRepricing, useEmployeePeople, useToday } from './useEmployeePricing';

interface Props {
    scopeType: 'TENANT' | 'COMPANY' | 'CONTRACT';
    contractId?: number;
    onClose: () => void;
    entityId?: number;
    onFormSumbit: (formData: PricelistDto, id?: string) => Promise<void>;
    classifiers: Record<string, ClassifierData>;
    deleteButton?: JSX.Element;
}

interface PricelistForm {
    name: string;
    company: { id: number; name: string } | null;
    currency: SelectType<string, string> | null;
    validFrom: string;
    validTo: string | null;
    scopeTypeCode: string;
    rates: HourlyRate[];
}

export function EditPricelistModal({
    scopeType,
    contractId,
    onClose,
    entityId,
    onFormSumbit,
    classifiers,
    deleteButton,
}: Props): JSX.Element {
    const { data: jobRoles, isLoading: isLoadingJobRoles } = useGetJobRolesListQuery();
    const { data: entityData, isLoading: isLoadingPricelist } = useGetPricelistByIdQuery(entityId ?? skipToken);
    const [isValidating, setIsValidating] = useState(false);
    const isEmployeePricingEnabled = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_PRICING);
    const today = useToday();

    const isContractPricelist = scopeType === ScopeType.CONTRACT;
    const isEditing = !!entityId;
    const pricingKind = editorPricingKind(entityData, !isEditing, isEmployeePricingEnabled);
    const isEmployeeMode = pricingKind === PricingKind.EMPLOYEE;

    const storedPeriods = useMemo(() => periodsFromList(entityData?.periods), [entityData]);
    const [periods, setPeriods] = useState<PeriodDraft[] | null>(null);
    const [activePeriodKey, setActivePeriodKey] = useState('first');
    const { people, roleOrder, isLoading: isLoadingPeople } = useEmployeePeople(entityData?.periods, !isEmployeeMode);
    const confirmPastRepricing = useConfirmPastRepricing(entityId);

    // Initialised once the stored list is in, with a render-time guard as the case editor does.
    if (isEmployeeMode && !isLoadingPricelist && periods === null) {
        setPeriods(storedPeriods);
        setActivePeriodKey(inForcePeriodOf(storedPeriods, today)?.key ?? storedPeriods[0].key);
    }

    const { code: defaultCurrencyCode, isLoading: isLoadingDefaultCurrency } = useDefaultCurrencyCode();
    const currencyOptions = useMemo<SelectType<string, string>[]>(
        () => (classifiers ? getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']) : []),
        [classifiers]
    );

    const defaultValues = useMemo<PricelistForm>(
        () => ({
            name: entityData?.name || '',
            company: entityData?.company ? { id: entityData.company.id, name: entityData.company.name } : null,
            // A new price list starts in the firm's default currency.
            currency: isEditing
                ? (currencyOptions.find((option) => option.value === entityData?.currencyTypeCode) ?? null)
                : defaultCurrencyOption(currencyOptions, defaultCurrencyCode),
            validFrom: formatDateStringInput(entityData?.validFrom ? new Date(entityData.validFrom) : new Date()),
            validTo: entityData?.validTo ? formatDateStringInput(new Date(entityData.validTo)) : null,
            scopeTypeCode: scopeType,
            rates:
                jobRoles
                    ?.filter((jobRole: JobRolesListElement) => {
                        const existingRate = entityData?.rates?.find((rate) => rate.jobRole.id === jobRole.id);
                        const hasExistingValue = existingRate?.hourlyPrice && existingRate.hourlyPrice !== '';

                        if (!entityId) {
                            return jobRole.isActive;
                        }

                        return jobRole.isActive || hasExistingValue;
                    })
                    .map((jobRole: JobRolesListElement) => ({
                        jobRole,
                        hourlyPrice:
                            entityData?.rates
                                ?.find((rate) => rate.jobRole.id === jobRole.id)
                                ?.hourlyPrice?.toString() ?? '',
                    })) || [],
        }),
        [entityData, jobRoles, currencyOptions, scopeType, entityId, isEditing, defaultCurrencyCode]
    );

    const { control, handleSubmit, setValue, watch } = useForm<PricelistForm>({
        defaultValues,
        values: defaultValues,
    });

    const rates = watch('rates');
    const dateErrors = useMemo(() => validatePeriodDates(periods ?? []), [periods]);

    const baseDto = (form: PricelistForm): PricelistDto => ({
        id: entityData?.id || 0,
        name: form.name,
        scopeTypeCode: form.scopeTypeCode,
        companyId: form.company?.id ?? null,
        companyName: form.company?.name ?? '',
        contractId: contractId || null,
        currencyTypeCode: form.currency?.value ?? null,
        currencyTypeName: form.currency?.label ?? '',
        validFrom: form.validFrom ? dateStringToDateObject(form.validFrom) : new Date(),
        validTo: form.validTo ? dateStringToDateObject(form.validTo) : null,
        rates: form.rates,
        pricingKind,
    });

    const onValidEmployee = (form: PricelistForm): void => {
        setIsValidating(true);
        const firstInvalid = (periods ?? []).find((period) => dateErrors[period.key]);
        if (!periods || firstInvalid) {
            if (firstInvalid) setActivePeriodKey(firstInvalid.key);
            errorAlert(t('pricelists.employee.check-period-dates'));
            return;
        }
        const dto: PricelistDto = {
            ...baseDto(form),
            validFrom: null,
            validTo: null,
            rates: [],
            periods: toPeriodWrites(periods),
        };
        void confirmPastRepricing(periods, storedPeriods, () => void onFormSumbit(dto, entityId?.toString()));
    };

    const onValid = (form: PricelistForm): void => {
        if (isEmployeeMode) {
            onValidEmployee(form);
            return;
        }
        setIsValidating(true);
        if (form.rates.find((rate) => !rate.hourlyPrice && rate.jobRole.isActive)) {
            errorAlert(t('validation.required-fields-empty'));
            return;
        }
        void onFormSumbit(baseDto(form), entityId?.toString());
    };

    const modelPricelistTitle = entityId ? t('pricelists.edit-model-price-list') : t('pricelists.model-price-list');

    if (
        isLoadingPricelist ||
        isLoadingJobRoles ||
        isLoadingDefaultCurrency ||
        (isEmployeeMode && (isLoadingPeople || !periods))
    ) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader
                title={isContractPricelist ? t('pricelists.edit-price-list') : modelPricelistTitle}
                onCancel={onClose}
            />
            <div className="flex overflow-y-auto">
                <div
                    className={
                        isEmployeeMode ? 'flex w-1/2 flex-col gap-4 px-6 pt-6' : 'flex w-7/12 flex-col gap-4 px-6 pt-6'
                    }
                >
                    {isEmployeeMode ? (
                        <>
                            <div>
                                <StatusChip type="neutral" label={t('pricelists.employee.per-employee')} />
                            </div>
                            <div className="text-grey-600 text-[0.8rem]">{t('pricelists.employee.intro')}</div>
                        </>
                    ) : null}
                    {isEditing ? (
                        <TextInput control={control} name="name" label="name" placeholder="name" required />
                    ) : null}
                    {!isContractPricelist ? (
                        <DynamicSelectInput
                            setValue={setValue}
                            control={control}
                            name="company"
                            label="company"
                            placeholder="forms.select-company"
                            paginated={{ fetch: useLazyGetCompaniesWithFiltersQuery, searchField: 'name' }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            clearable
                            onChange={(value) =>
                                setValue('scopeTypeCode', value ? ScopeType.COMPANY : ScopeType.TENANT)
                            }
                        />
                    ) : null}
                    <div className="w-3/5">
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            name="currency"
                            label="currency"
                            placeholder="pricelists.select-currency"
                            options={currencyOptions}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            required={!(isContractPricelist && isEditing)}
                            readonly={isContractPricelist && isEditing}
                        />
                    </div>
                    {!isContractPricelist && !isEmployeeMode ? (
                        <>
                            <div className="w-3/5">
                                <DateInput
                                    control={control}
                                    setValue={setValue}
                                    name="validFrom"
                                    label="valid-from"
                                    required
                                    fullWidth
                                />
                            </div>
                            <div className="w-3/5">
                                <DateInput
                                    control={control}
                                    setValue={setValue}
                                    name="validTo"
                                    label="valid-to"
                                    fullWidth
                                />
                            </div>
                        </>
                    ) : null}
                </div>
                {isEmployeeMode && periods ? (
                    <div className="h-[465px] w-1/2 overflow-y-auto border-l border-zinc-300 px-4 py-3.5">
                        <EmployeeRatesEditForm
                            periods={periods}
                            activeKey={activePeriodKey}
                            onActiveKeyChange={setActivePeriodKey}
                            onPeriodsChange={setPeriods}
                            people={people}
                            roleOrder={roleOrder}
                            dateErrors={dateErrors}
                            isValidating={isValidating}
                            today={today}
                        />
                    </div>
                ) : (
                    <div className="w-5/12 border-l border-zinc-300 pl-4">
                        <HourlyRatesEditForm
                            isValidating={isValidating}
                            onChange={(value) => setValue('rates', value)}
                            hourlyRates={rates}
                            classifiers={classifiers}
                            height={465}
                        />
                    </div>
                )}
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => {
                    void handleSubmit(onValid)();
                }}
                extraButton={deleteButton}
            />
        </>
    );
}
